/**
 * Explicit checklist row hierarchy for Program Templates.
 * level 0 = main / heading / section; 1 = lettered sub; 2 = roman under letter.
 * kind "note" / "heading" / "section" — no answer cells on Program (heading keeps num + text).
 */

export type ChecklistRowLevel = 0 | 1 | 2;

export interface ChecklistRowLike {
    id?: string;
    kind?: string;
    num?: string;
    text?: string;
    level?: number;
}

/** MUI spacing units — matches prior BChecklistView deepIndent (0 / 1 / 2.5). */
export function indentPaddingLeft(level: number | undefined | null): number {
    const n = typeof level === 'number' && level > 0 ? Math.min(2, Math.floor(level)) : 0;
    if (n <= 0) return 0;
    if (n === 1) return 1;
    return 2.5;
}

function normalizeNum(num: string | undefined): string {
    return (num || '').trim().toLowerCase();
}

/**
 * One-time backfill when `level` is missing (legacy DB / code defaults).
 * Renderers must not call letter heuristics — only this migrator may.
 */
export function inferRowLevel(
    row: ChecklistRowLike,
    prev: ChecklistRowLike | undefined,
    next: ChecklistRowLike | undefined,
): ChecklistRowLevel {
    if (typeof row.level === 'number' && row.level >= 0 && row.level <= 2) {
        return Math.floor(row.level) as ChecklistRowLevel;
    }

    const kind = (row.kind || '').toLowerCase();
    if (kind === 'heading') {
        const n = normalizeNum(row.num);
        if (/^\(?i{1,3}\)?$/.test(n) || /^i{1,3}\)$/.test(n)) return 2;
        if (/^\(?[a-z]\)?$/.test(n) || /^[a-z]\)$/.test(n)) return 1;
        return 0;
    }
    if (kind !== 'sub') {
        return 0;
    }

    const n = normalizeNum(row.num);
    const prevN = normalizeNum(prev?.num);
    const nextN = normalizeNum(next?.num);

    if (/^i{2,3}\)$/.test(n)) return 2;
    if (n === 'i)') {
        if (prevN === 'h)') return 1; // alphabetical a)…i)
        if (nextN === 'ii)') return 2;
        if (/^[a-g]\)$/.test(prevN)) return 2;
        return 1;
    }

    // (a) / a) lettered subs
    return 1;
}

export function normalizeChecklistRows<T extends ChecklistRowLike>(rows: T[]): T[] {
    return rows.map((row, idx) => {
        if (typeof row.level === 'number' && row.level >= 0 && row.level <= 2) {
            return row;
        }
        const level = inferRowLevel(row, rows[idx - 1], rows[idx + 1]);
        return {...row, level};
    });
}

/** Normalize `content.rows` (and leave other fields intact). */
export function normalizeTemplateContent(
    content: Record<string, unknown>,
): Record<string, unknown> {
    if (!content || typeof content !== 'object') return content;
    if (!Array.isArray(content.rows)) return content;
    return {
        ...content,
        rows: normalizeChecklistRows(content.rows as ChecklistRowLike[]),
    };
}

/** Resolve display level for a row (prefer explicit; else kind===sub → 1). */
export function resolveDisplayLevel(row: ChecklistRowLike): number {
    if (typeof row.level === 'number' && row.level >= 0) {
        return Math.min(2, Math.floor(row.level));
    }
    return row.kind === 'sub' ? 1 : 0;
}
